/**
 * Optimized script loader for third-party widgets following Web.dev best practices
 * https://web.dev/articles/efficiently-load-third-party-javascript
 */

interface ScriptLoadingOptions {
	src: string;
	content?: Record<string, unknown>;
	container: HTMLElement;
	loadingMessage?: string;
	errorMessage?: string;
	priority?: "high" | "low";
	timeout?: number;
}

/**
 * Load a third-party script with performance optimizations
 */
export function loadOptimizedScript({
	src,
	content,
	container,
	loadingMessage = "Loading...",
	errorMessage = "Widget temporarily unavailable",
	priority = "high",
	timeout = 2000,
}: ScriptLoadingOptions): void {
	const loadWidget = () => {
		// Show loading indicator
		container.innerHTML = `<div class="p-4 text-center text-text">${loadingMessage}</div>`;

		const script = document.createElement("script");
		script.src = src;
		script.type = "text/javascript";
		script.async = true;
		script.defer = true;

		// Add error handling
		script.onerror = () => {
			console.warn(`Failed to load script: ${src}`);
			container.innerHTML = `<div class="p-4 text-center text-text">${errorMessage}</div>`;
		};

		// Add content if provided
		if (content) {
			script.text = JSON.stringify(content);
		}

		// Find the container's parent or use container itself
		const targetElement = container.parentElement || container;
		targetElement.appendChild(script);
	};

	// Use appropriate loading strategy based on priority
	const timeoutMs = priority === "low" ? timeout + 1000 : timeout;
	const delayMs = priority === "low" ? 200 : 100;

	// Use requestIdleCallback for better performance, fallback to setTimeout
	if ("requestIdleCallback" in window && window.requestIdleCallback) {
		window.requestIdleCallback(loadWidget, { timeout: timeoutMs });
	} else {
		setTimeout(loadWidget, delayMs);
	}
}

/**
 * Preload critical third-party resources
 */
export function preloadThirdPartyResources(domains: string[]): void {
	for (const domain of domains) {
		// Create DNS prefetch link
		const prefetch = document.createElement("link");
		prefetch.rel = "dns-prefetch";
		prefetch.href = `//${domain}`;
		document.head.appendChild(prefetch);

		// Create preconnect link for critical domains
		const preconnect = document.createElement("link");
		preconnect.rel = "preconnect";
		preconnect.href = `https://${domain}`;
		preconnect.crossOrigin = "anonymous";
		document.head.appendChild(preconnect);
	}
}

/**
 * Clean up script loading resources
 */
export function cleanupScriptResources(container: HTMLElement): void {
	if (container) {
		container.innerHTML = "";
	}
}
